import PropTypes from 'prop-types';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { errorMessage } from '../../actions/internal';
import { useCommunication } from '../../hooks/useCommunication';
import CardSelector from './CardSelector';
import ProgramContentSelector from './ProgramContentSelector';

const MessagesPanel = ({
  conversationId,
  onSendMessage,
  player,
  errorMessage,
  isSending = false,
}) => {
  const { newMessageModal } = useCommunication();

  const [message, setMessage] = useState('');
  const [cards, setCards] = useState([]);
  const [programContentEntries, setProgramContentEntries] = useState([]);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [showCards, setShowCards] = useState(false);
  const [showTips, setShowTips] = useState(false);
  const [dragHeight, setDragHeight] = useState(null); // null = not dragged, use auto
  const attachRef = useRef(null);
  const textareaRef = useRef(null);
  const isDragging = useRef(false);
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(0);

  const HARD_MAX = Math.round(window.innerHeight * 0.7);
  const MIN_HEIGHT = 36;

  // Auto-resize textarea to fit content when typing (only when not drag-locked)
  useEffect(() => {
    const el = textareaRef.current;
    if (!el || dragHeight !== null) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, HARD_MAX)}px`;
  }, [message, dragHeight]);

  // Apply drag-set height
  useEffect(() => {
    const el = textareaRef.current;
    if (!el || dragHeight === null) return;
    el.style.height = `${dragHeight}px`;
  }, [dragHeight]);

  // Reset on send
  useEffect(() => {
    if (!message && textareaRef.current) {
      textareaRef.current.style.height = `${MIN_HEIGHT}px`;
      setDragHeight(null);
    }
  }, [message]);

  const onDragStart = useCallback((e) => {
    isDragging.current = true;
    dragStartY.current = e.clientY;
    dragStartHeight.current = textareaRef.current
      ? textareaRef.current.offsetHeight
      : MIN_HEIGHT;
    e.preventDefault();
  }, []);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      const delta = dragStartY.current - e.clientY;
      const newHeight = Math.min(HARD_MAX, Math.max(MIN_HEIGHT, dragStartHeight.current + delta));
      setDragHeight(newHeight);
    };
    const onMouseUp = () => { isDragging.current = false; };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const onModalClose = (keepInput) => {
    if (keepInput) return;
    setMessage('');
    setCards([]);
    setProgramContentEntries([]);
    setShowCards(false);
    setShowTips(false);
  }

  const hasContent = cards.length > 0 || programContentEntries.length > 0;

  const onSendClick = () => {
    if (!message.trim() && !hasContent) {
      return;
    }

    onSendMessage(conversationId, {
      message,
      cardIds: cards.map(c => c.value),
      programContentEntryIds: programContentEntries.map(e => e.value),
      contentAvailable: true,
    });
    setMessage('');
    setCards([]);
    setProgramContentEntries([]);
    setShowCards(false);
    setShowTips(false);
  }

  const onTranslateClick = () => {
    if (!message.trim()) return;
    newMessageModal({
      conversationId,
      player,
      message,
      originCards: cards,
      originProgramContentEntries: programContentEntries,
      forcePreview: true,
      onModalClose,
    });
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSendClick();
    }
  };

  const hasAttachments = showCards || showTips;

  return (
    <div className="o-communication__message-bar">
      <div className="o-communication__message-bar-drag-handle" onMouseDown={onDragStart} />
      {hasAttachments && (
        <div className="o-communication__message-bar-attachments">
          {showCards && (
            <div className="o-communication__message-bar-attachment-row">
              <span className="o-communication__message-bar-attachment-label">
                <i className="fa fa-clone" aria-hidden="true"></i> Cards
              </span>
              <div className="o-communication__message-bar-attachment-selector">
                <CardSelector
                  value={cards}
                  onChange={setCards}
                  isDisabled={isSending || showTips}
                  onErrorMessage={errorMessage}
                />
              </div>
              <button
                className="o-communication__message-bar-attachment-remove"
                onClick={() => { setShowCards(false); setCards([]); }}
                type="button"
                title="Remove"
              >
                <i className="fa fa-times" aria-hidden="true"></i>
              </button>
            </div>
          )}
          {showTips && (
            <div className="o-communication__message-bar-attachment-row">
              <span className="o-communication__message-bar-attachment-label">
                <i className="fa fa-lightbulb-o" aria-hidden="true"></i> Tips
              </span>
              <div className="o-communication__message-bar-attachment-selector">
                <ProgramContentSelector
                  value={programContentEntries}
                  onChange={setProgramContentEntries}
                  isDisabled={isSending || showCards}
                  onErrorMessage={errorMessage}
                />
              </div>
              <button
                className="o-communication__message-bar-attachment-remove"
                onClick={() => { setShowTips(false); setProgramContentEntries([]); }}
                type="button"
                title="Remove"
              >
                <i className="fa fa-times" aria-hidden="true"></i>
              </button>
            </div>
          )}
        </div>
      )}

      <div className="o-communication__message-bar-row">
        <div className="o-communication__message-bar-attach" ref={attachRef}>
          <button
            className="o-communication__message-bar-attach-btn"
            onClick={() => setAttachMenuOpen(o => !o)}
            title="Add attachment"
            type="button"
          >
            <i className="fa fa-plus" aria-hidden="true"></i>
          </button>
          {attachMenuOpen && (
            <div className="o-communication__message-bar-attach-menu">
              <button
                className="o-communication__message-bar-attach-option"
                type="button"
                onClick={() => {
                  setShowCards(true);
                  setAttachMenuOpen(false);
                }}
                disabled={showTips}
              >
                <i className="fa fa-clone" aria-hidden="true"></i>
                <span>Cards</span>
              </button>
              <button
                className="o-communication__message-bar-attach-option"
                type="button"
                onClick={() => {
                  setShowTips(true);
                  setAttachMenuOpen(false);
                }}
                disabled={showCards}
              >
                <i className="fa fa-lightbulb-o" aria-hidden="true"></i>
                <span>Tips</span>
              </button>
            </div>
          )}
        </div>

        <textarea
          ref={textareaRef}
          className="o-communication__message-bar-input"
          placeholder="Enter message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
        />

        <div className="o-communication__message-bar-actions">
          <button
            className="o-communication__message-bar-btn o-communication__message-bar-btn--translate"
            onClick={onTranslateClick}
            disabled={!message.trim()}
            title="Translate"
            type="button"
          >
            <i className="fa fa-language" aria-hidden="true"></i>
            <span>Translate</span>
          </button>
          <button
            className="o-communication__message-bar-btn o-communication__message-bar-btn--send"
            onClick={onSendClick}
            disabled={!message.trim()}
            title="Send"
            type="button"
          >
            <i className="fa fa-paper-plane" aria-hidden="true"></i>
            <span>Send</span>
          </button>
        </div>
      </div>

      {attachMenuOpen && (
        <div
          className="o-communication__message-bar-attach-backdrop"
          onClick={() => setAttachMenuOpen(false)}
        />
      )}
    </div>
  );
};

MessagesPanel.propTypes = {
  conversationId: PropTypes.number.isRequired,
  player: PropTypes.shape({
    playerId: PropTypes.number,
    playerName: PropTypes.string,
    accountName: PropTypes.string,
  }).isRequired,
  onSendMessage: PropTypes.func.isRequired,
  isSending: PropTypes.bool,
  errorMessage: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  isSending: state.communication?.isSending,
});

export default connect(mapStateToProps, {
  errorMessage,
})(MessagesPanel);
